import VueRouter from "vue-router";
import store from "../store";

// Routes
import Signup from "./signup";
import ForgotPassword from "./forgot-password";
import NewPassword from "./new-password";
import Activate from "./activate";
import Verify from "./verify";
import FiveOhOh from "./five-oh-oh";

// Dashboard
import Home from "./dashboard/home";
import Dashboard from "./dashboard";
import Account from "./dashboard/account";
import Organisation from "./dashboard/organisation";
import Tests from "./dashboard/tests";
import CreateTest from "./dashboard/create-test";
import CreateWebTest from "./dashboard/create-test/web";
import CreateVideoTest from "./dashboard/create-test/video";
import Success from "./dashboard/success";
import Members from "./dashboard/members";
import AddMember from "./dashboard/add-member";
import EditMember from "./dashboard/edit-member";
import Demo from "./dashboard/demo";
import Development from "./dashboard/development";
import Suggestions from "./dashboard/test/suggestions";
import Suggestion from "./dashboard/test/suggestions/suggestion";
import Resources from "./dashboard/resources";
import Article from "./dashboard/resources/article";
import EditArticle from "./dashboard/resources/edit";

// Research
import Research from "./dashboard/research";
import ResearchParticipants from "./dashboard/research/research-participants";
import ResearchParticipant from "./dashboard/research/research-participants/research-participant";
import TestSetup from "./dashboard/research/test-setup";
import ResearchHeatmaps from "./dashboard/research/heatmaps";
import ResearchInsights from "./dashboard/research/insights";
import ResearchPages from "./dashboard/research/pages";
import ResearchQuestionnaire from "./dashboard/research/questionnaire";
import ResearchSuggestions from "./dashboard/research/suggestions";
import ResearchTags from "./dashboard/research/tags";
import Files from "./dashboard/research/files";
import InsightWizard from "./dashboard/research/insights_wizard";
import Scenes from "./dashboard/research/scenes";
import QualitativeFeedback from "./dashboard/research/qualitative-feedback";
import DynamicAnalysis from "./dashboard/research/dynamic-analysis";
import Moments from "./dashboard/research/moments";

// Test
import Overview from "./dashboard/test/overview";
import Video from "./dashboard/test/video";
import Tags from "./dashboard/test/tags";
import Benchmark from "./dashboard/test/benchmark";
import Journey from "./dashboard/test/journey";
import PageInsights from "./dashboard/test/page-insights";
import Insights from "./dashboard/test/insights";
import Insight from "./dashboard/test/insights/insight";
import TestDemo from "./dashboard/test/demo";
import Locked from "./dashboard/test/locked";
import Heatmap from "./dashboard/test/heatmap";
import SectionVideo from "./dashboard/test/section-video";
import Participants from "./dashboard/test/participants";

// Video Test
import VideoTestSummary from "./dashboard/video-test/summary";
import VideoTestScenes from "./dashboard/video-test/scenes";
import VideoTestDynamicAnalysis from "./dashboard/video-test/dynamic-analysis";
import VideoTestParticipant from "./dashboard/video-test/participant";
import VideoTestParticipants from "./dashboard/video-test/participants";
import VideoTestQualitativeFeedback from "./dashboard/video-test/qualitative-feedback";
import VideoTestMoments from "./dashboard/video-test/moments";

// Test Setups
import TestSetups from "./dashboard/test-setups";

// Admin
import Companies from "./dashboard/admin/companies";
import Company from "./dashboard/admin/companies/company";
import NewCompany from "./dashboard/admin/companies/new-company";
import Users from "./dashboard/admin/users";
import User from "./dashboard/admin/users/user";
import newUser from "./dashboard/admin/users/newuser";

// Predictions
import Predictions from "./dashboard/predictions/index.vue";
import Project from "./dashboard/predictions/project";
import PredictionHeatmap from "./dashboard/predictions/heatmap";
import PredictionHeatmaps from "./dashboard/predictions/heatmaps";
import Projects from "./dashboard/predictions/projects";
import CompareHeatmaps from "./dashboard/predictions/compare";

VueRouter.prototype.open = function(routeObject) {
  const { href } = this.resolve(routeObject);
  window.open(href, "_blank");
};

const testGuard = async (to, from, next) => {
  const testId = to.params.id;

  if (store.state.tests.tests.length === 0) {
    await store.dispatch("fetchAllTests");
  }

  if (
    store.state.user.user.role === "demo" &&
    !to.fullPath.includes("demo") &&
    !from.fullPath.includes("/test/") &&
    !from.fullPath.includes("/video-test/")
  ) {
    if (to.fullPath.includes("/test/")) {
      next(`/test/${to.params.id}/demo`);
    }
  }

  // essential vs pro
  const isLocked = to.matched.some((record) => record.meta.isProfessional);
  if (store.state.test.test_type === "essential" && isLocked) {
    next(`/test/${to.params.id}/locked`);
    return null;
  }

  // fetches test to store
  if (testId && parseInt(testId) !== store.state.test.id) {
    const fetch = await store.dispatch("fetchTest", to.params.id);
    if (fetch) {
      next();
    } else {
      next("/tests");
    }
  } else {
    next();
  }
};

const licenceGuard = async (to, from, next) => {
  if (store.state.user.user.company.licence_analysis) {
    next();
  } else {
    next("/");
  }
};

const demoGuard = async (to, from, next) => {
  // eslint-disable-next-line no-console
  console.log(store.state.user.user.company && store.state.user.user.company.name)
  if (store.state.user.user.company && store.state.user.user.company.name != "Demo") {
    next();
  } else {
    next("/");
  }
};

const licenseTestGuard = async (to, from, next) => {
  const testId = to.params.id;

  if (store.state.tests.tests.length === 0) {
    await store.dispatch("fetchAllTests");
  }

  if (store.state.user.user.company.licence_research) {
    if (testId && parseInt(testId) !== store.state.test.id) {
      const fetch = await store.dispatch("fetchTest", to.params.id);
      if (fetch) {
        next();
      } else {
        next("/tests");
      }
    } else {
      next();
    }
  } else {
    next("/");
  }
};

const routes = [
  { path: "/signup", component: Signup },
  { path: "/forgot-password", component: ForgotPassword },
  { path: "/new-password", component: NewPassword },
  { path: "/activate", component: Activate },
  { path: "/500", component: FiveOhOh },
  { path: "/verify", component: Verify },
  {
    path: "/",
    name: "dashboard",
    component: Dashboard,
    meta: {
      requiresAuth: true,
    },
    redirect: "/home",
    children: [
      {
        path: "/home",
        component: Home,
      },
      {
        path: "/admin/companies",
        component: Companies,
        beforeEnter: licenceGuard,
      },
      {
        path: "/admin/companies/new",
        component: NewCompany,
        beforeEnter: licenceGuard,
      },
      {
        path: "/admin/companies/:id",
        component: Company,
        beforeEnter: licenceGuard,
      },
      {
        path: "/admin/users",
        component: Users,
        beforeEnter: licenceGuard,
      },
      {
        path: "/admin/users/new",
        component: newUser,
        beforeEnter: licenceGuard,
      },
      {
        path: "/admin/users/:id",
        component: User,
        beforeEnter: licenceGuard,
      },
      {
        path: "test/:id/demo",
        component: TestDemo,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/locked",
        component: Locked,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/benchmark",
        component: Benchmark,
        beforeEnter: testGuard,
        meta: {
          isProfessional: true,
        },
      },
      {
        path: "test/:id/participant/:participantId/heatmap",
        component: Heatmap,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/participants",
        component: Participants,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/overview",
        component: Overview,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/page-insights",
        component: PageInsights,
        beforeEnter: testGuard,
        meta: {
          isProfessional: true,
        },
      },
      {
        path: "test/:id/participant/:participantId/recording",
        component: Video,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/participant/:participantId/video",
        component: Video,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/journey",
        component: Journey,
        beforeEnter: testGuard,
        meta: {
          isProfessional: true,
        },
      },
      {
        path: "test/:id/section-video",
        component: SectionVideo,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/tags/:tagId?",
        component: Tags,
        beforeEnter: testGuard,
        meta: {
          isProfessional: true,
        },
      },
      {
        path: "test/:id/insights",
        component: Insights,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/suggestions",
        component: Suggestions,
        beforeEnter: testGuard,
      },

      {
        path: "test/:id/suggestions/:suggestionId",
        component: Suggestion,
        beforeEnter: testGuard,
      },
      {
        path: "test/:id/insights/:insightId",
        component: Insight,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/summary",
        component: VideoTestSummary,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/scenes",
        component: VideoTestScenes,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/dynamic-analysis",
        component: VideoTestDynamicAnalysis,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/participant/:participantId",
        component: VideoTestParticipant,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/participant/",
        component: VideoTestParticipants,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/participant/:participantId",
        component: VideoTestParticipant,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/qualitative-feedback",
        component: VideoTestQualitativeFeedback,
        beforeEnter: testGuard,
      },
      {
        path: "video-test/:id/moments",
        component: VideoTestMoments,
        beforeEnter: testGuard,
      },
      {
        path: "account",
        component: Account,
      },
      {
        path: "development",
        component: Development,
      },
      {
        path: "tests",
        component: Tests,
      },
      {
        path: "resources",
        component: Resources,
      },
      {
        path: "resources/new",
        component: EditArticle,
        beforeEnter: licenceGuard,
      },
      {
        path: "resources/edit",
        redirect: "/resources",
        beforeEnter: licenceGuard,
      },
      {
        path: "resources/:id/edit",
        component: EditArticle,
        beforeEnter: licenceGuard,
      },
      {
        path: "resources/:id",
        component: Article,
      },
      {
        path: "demo",
        component: Demo,
      },
      {
        path: "research",
        component: Research,
        beforeEnter: licenceGuard,
      },
      {
        path: "research/test/:id/test-setup",
        component: TestSetup,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/participants",
        component: ResearchParticipants,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/participants/:participantId",
        component: ResearchParticipant,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/heatmaps",
        component: ResearchHeatmaps,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/insights",
        component: ResearchInsights,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/pages",
        component: ResearchPages,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/questionnaire",
        component: ResearchQuestionnaire,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/suggestions",
        component: ResearchSuggestions,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/tags",
        component: ResearchTags,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/files",
        component: Files,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/scenes",
        component: Scenes,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/insights-wizard",
        component: InsightWizard,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/qualitative-feedback",
        component: QualitativeFeedback,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/dynamic-analysis",
        component: DynamicAnalysis,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "research/test/:id/moments",
        component: Moments,
        beforeEnter: licenseTestGuard,
      },
      {
        path: "predictions",
        component: Predictions,
      },
      {
        path: "predictions/projects",
        component: Projects,
      },
      {
        path: "predictions/heatmaps/:heatmapId/compare",
        component: CompareHeatmaps,
      },
      {
        path: "predictions/heatmaps",
        component: PredictionHeatmaps,
      },
      {
        path: "predictions/projects/:id",
        component: Project,
      },
      {
        path: "predictions/heatmaps/:heatmapId",
        component: PredictionHeatmap,
      },
      {
        path: "predictions/projects/:projectId/heatmaps/:heatmapId",
        component: PredictionHeatmap,
      },
      {
        path: "organisation",
        component: Organisation,
        beforeEnter: demoGuard,
      },
      {
        path: "members",
        component: Members,
        exact: true,
        beforeEnter: demoGuard,
      },
      {
        path: "add-member",
        component: AddMember,
        exact: true,
      },
      {
        path: "edit-member/:id",
        component: EditMember,
        exact: true,
      },
      { path: "/create-test/web/:id?", component: CreateWebTest },
      { path: "/create-test/video/:id?", component: CreateVideoTest },
      { path: "/create-test/", component: CreateTest },
      { path: "/success/", component: Success },
      { path: "/research/testsetups/", component: TestSetups },
    ],
  },
];

const router = new VueRouter({
  mode: "history",
  routes: routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Auth is required
  if (requiresAuth) {
    if (!store.state.user.hasToken) {
      next("signup");
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
